import { FaroButton } from "@components/common/faro-button";
import { useOrbisWorkerApi } from "@api/orbis-worker-api-client";
import { UUID } from "@stellar/api-logic";
import { GUID } from "@faro-lotv/ielement-types";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@hooks/use-toast";
import { getDevButtonStyle } from "@pages/data-management/data-management-ui-utils";
import { LocalStorageStringName, LocalStorageUtils } from "@utils/local-storage-utils";
import { runtimeConfig } from "@src/runtime-config";
import pLimit from "p-limit";
import { OrbisProcessingSuccessResponse } from "@api/orbis-worker-api-client";
import { AxiosError } from "axios";

const MAX_ORBIS_SCANS_PROCESSING = 8;
const storageItem: LocalStorageStringName = `orbis-processing-code-${runtimeConfig.appEnv}`;

const orbisProcessingLimiter = pLimit(MAX_ORBIS_SCANS_PROCESSING);

interface Props {
  /** The selected table items, which may include ones currently not eligible for removal. */
  selectedItems: string[];
  /** Callback to determine if processing of Orbis scans has been initiated */
  setIsProcessOrbisBtnClicked: React.Dispatch<React.SetStateAction<boolean>>;
  /** UserId initating the processing */
  userId: UUID;
  /** ProjectId of the associated project */
  projectId: GUID;
}

/** Button to remove scans in the Staging Area in bulk. */
export function ProcessOrbisScansButton({
  selectedItems,
  setIsProcessOrbisBtnClicked,
  userId,
  projectId,
}: Props): JSX.Element {
  // iterate through selectedItems and filter all orbis scans
  const { processOrbisScan } = useOrbisWorkerApi({
    userId: userId,
    projectId: projectId,
  });

  const { handleErrorWithToast } = useErrorContext();
  const { showToast } = useToast();

  async function onClick(): Promise<void> {
    let authorizationCode = LocalStorageUtils.getStringItem(storageItem) ?? undefined;
    if (!authorizationCode) {
      // prompt the user for authorization code to trigger orbis processing
      // eslint-disable-next-line no-alert -- should prompt the user for authorization code
      authorizationCode = prompt("Enter the authorization code to trigger Orbis processing worker")?.trim();
      // user didn't enter any auth code or clicked on Cancel button
      if (!authorizationCode) {
        // reset the table
        setIsProcessOrbisBtnClicked(true);
        return;
      }
      // store the code in the browser
      LocalStorageUtils.setStringItem(storageItem, authorizationCode);
    }

    // check if no items are selected
    if (!selectedItems.length){
      showToast({
        message: "No orbis scans selected",
        type: "error",
      });
      return;
    }
    // callback to initiate processing of orbis scans
    try {
      const orbisProcessingResults: Promise<OrbisProcessingSuccessResponse>[] =
        selectedItems.map((item) => {
          return orbisProcessingLimiter(async(): Promise<OrbisProcessingSuccessResponse> => {
            return processOrbisScan(item, authorizationCode);
          });
        });

      await Promise.all(orbisProcessingResults);

      // show success toast
      showToast({
        message: "Orbis processing has successfully started",
        type: "success",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `triggerOrbisProcessingWorker-${Date.now().toString()}`,
        title: "Error triggering orbis worker",
        error: (error as AxiosError).message,
      });
      // most-likely the auth code is wrong
      LocalStorageUtils.removeItem(storageItem);
      return;
    } finally {
      setIsProcessOrbisBtnClicked(true);
    }
  }

  return (
    <FaroButton
      onClick={onClick}
      sx={getDevButtonStyle()}
      shouldNotTranslate={true}
    >
      Process Orbis Scans
    </FaroButton>
  );
}
